/**
 * The report an uptime checker sends to Cortex: the state of every target it
 * probes, its open and recent incidents, and its daily check counts. The
 * checker builds it, the Cortex backend validates it with
 * `normalizeUptimeReport`, and the frontend renders it.
 */

export const UPTIME_REPORT_VERSION = 1;

/** Checker and target identifiers share this pattern. */
export const UPTIME_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}$/;

export const ENVIRONMENTS = ['production', 'staging', 'dev'] as const;
export type Environment = (typeof ENVIRONMENTS)[number];

/** `unknown` until the first check of a target completes. */
export type TargetStatus = 'up' | 'down' | 'unknown';

export interface CheckResult {
  ok: boolean;
  checkedAt: string;
  /** Time to the response headers of the main request. Null when it never answered. */
  latencyMs: number | null;
  statusCode: number | null;
  /** Why the check failed; null when it passed. */
  error: string | null;
}

export interface TargetReport {
  id: string;
  name: string;
  environment: Environment;
  url: string;
  status: TargetStatus;
  /** When the target entered its current status. */
  since: string | null;
  last: CheckResult | null;
  /** The TLS certificate's expiry, checked once a day. Null for plain HTTP or before the first check. */
  certificateExpiresAt: string | null;
}

export interface Incident {
  target: string;
  /** The first failed check of the streak that took the target down. */
  startedAt: string;
  /** The first passing check after it; null while the target is down. */
  endedAt: string | null;
  error: string;
}

/** Checks of one target on one UTC day. */
export interface DayReport {
  date: string;
  target: string;
  checks: number;
  failures: number;
}

export interface UptimeReport {
  version: typeof UPTIME_REPORT_VERSION;
  checker: { id: string; label: string };
  generatedAt: string;
  intervalSeconds: number;
  targets: TargetReport[];
  /** Incidents that started in the last 90 days, plus any still open. */
  incidents: Incident[];
  /** The last 90 days. */
  days: DayReport[];
}

/** How many days of history a report carries. */
export const REPORT_DAYS = 90;

export class UptimeReportError extends Error {}

const LIMITS = { targets: 200, incidents: 5_000, days: 200 * (REPORT_DAYS + 1), text: 2048 };
const DATE = /^\d{4}-\d{2}-\d{2}$/;

type Json = Record<string, unknown>;

function object(value: unknown, path: string): Json {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new UptimeReportError(`${path} must be an object`);
  return value as Json;
}

function text(value: unknown, path: string): string {
  if (typeof value !== 'string' || value.length > LIMITS.text)
    throw new UptimeReportError(`${path} must be a string of at most ${LIMITS.text} characters`);
  return value;
}

function optionalText(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : text(value, path);
}

function id(value: unknown, path: string): string {
  const result = text(value, path);
  if (!UPTIME_ID_PATTERN.test(result)) throw new UptimeReportError(`${path} is not an ID`);
  return result;
}

function timestamp(value: unknown, path: string): string {
  const result = text(value, path);
  if (Number.isNaN(Date.parse(result))) throw new UptimeReportError(`${path} must be a timestamp`);
  return result;
}

function optionalTimestamp(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : timestamp(value, path);
}

function count(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0)
    throw new UptimeReportError(`${path} must be a non-negative number`);
  return value;
}

function optionalCount(value: unknown, path: string): number | null {
  return value === null || value === undefined ? null : count(value, path);
}

function list<T>(value: unknown, path: string, max: number, item: (v: unknown, p: string) => T) {
  if (!Array.isArray(value)) throw new UptimeReportError(`${path} must be a list`);
  if (value.length > max) throw new UptimeReportError(`${path} has more than ${max} entries`);
  return value.map((entry, index) => item(entry, `${path}[${index}]`));
}

function result(value: unknown, path: string): CheckResult | null {
  if (value === null || value === undefined) return null;
  const data = object(value, path);
  if (typeof data.ok !== 'boolean') throw new UptimeReportError(`${path}.ok must be a boolean`);
  return {
    ok: data.ok,
    checkedAt: timestamp(data.checkedAt, `${path}.checkedAt`),
    latencyMs: optionalCount(data.latencyMs, `${path}.latencyMs`),
    statusCode: optionalCount(data.statusCode, `${path}.statusCode`),
    error: optionalText(data.error, `${path}.error`),
  };
}

function target(value: unknown, path: string): TargetReport {
  const data = object(value, path);
  const environment = data.environment as Environment;
  if (!ENVIRONMENTS.includes(environment))
    throw new UptimeReportError(`${path}.environment must be one of ${ENVIRONMENTS.join(', ')}`);
  const status = data.status as TargetStatus;
  if (!['up', 'down', 'unknown'].includes(status))
    throw new UptimeReportError(`${path}.status must be up, down or unknown`);
  return {
    id: id(data.id, `${path}.id`),
    name: text(data.name, `${path}.name`),
    environment,
    url: text(data.url, `${path}.url`),
    status,
    since: optionalTimestamp(data.since, `${path}.since`),
    last: result(data.last, `${path}.last`),
    certificateExpiresAt: optionalTimestamp(
      data.certificateExpiresAt,
      `${path}.certificateExpiresAt`
    ),
  };
}

function incident(value: unknown, path: string): Incident {
  const data = object(value, path);
  return {
    target: id(data.target, `${path}.target`),
    startedAt: timestamp(data.startedAt, `${path}.startedAt`),
    endedAt: optionalTimestamp(data.endedAt, `${path}.endedAt`),
    error: text(data.error, `${path}.error`),
  };
}

function day(value: unknown, path: string): DayReport {
  const data = object(value, path);
  const date = text(data.date, `${path}.date`);
  if (!DATE.test(date)) throw new UptimeReportError(`${path}.date must be YYYY-MM-DD`);
  const checks = count(data.checks, `${path}.checks`);
  const failures = count(data.failures, `${path}.failures`);
  if (failures > checks) throw new UptimeReportError(`${path} has more failures than checks`);
  return { date, target: id(data.target, `${path}.target`), checks, failures };
}

/** Validate an untrusted report and return a copy holding only known fields. */
export function normalizeUptimeReport(value: unknown): UptimeReport {
  const data = object(value, 'report');
  if (data.version !== UPTIME_REPORT_VERSION)
    throw new UptimeReportError(`report.version must be ${UPTIME_REPORT_VERSION}`);
  const checker = object(data.checker, 'report.checker');
  const intervalSeconds = count(data.intervalSeconds, 'report.intervalSeconds');
  if (!Number.isInteger(intervalSeconds) || intervalSeconds < 10)
    throw new UptimeReportError('report.intervalSeconds must be a whole number of at least 10');
  return {
    version: UPTIME_REPORT_VERSION,
    checker: {
      id: id(checker.id, 'report.checker.id'),
      label: text(checker.label, 'report.checker.label'),
    },
    generatedAt: timestamp(data.generatedAt, 'report.generatedAt'),
    intervalSeconds,
    targets: list(data.targets, 'report.targets', LIMITS.targets, target),
    incidents: list(data.incidents, 'report.incidents', LIMITS.incidents, incident),
    days: list(data.days, 'report.days', LIMITS.days, day),
  };
}

/** Percent of passed checks, or null when there were none. */
export function uptimePercent(checks: number, failures: number): number | null {
  return checks ? ((checks - failures) / checks) * 100 : null;
}
